import React, {useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {Button, FlexColumn, FlexRow, Heading, SegmentedControl, Table, Text} from '@forecast-it/design-system';
import {filterAssets, toRows, type AssetFilter} from './clientAssetPresentation';
import type {ClientAssetsPage_project$key} from './__generated__/ClientAssetsPage_project.graphql';

export const ClientAssetsPageQuery = graphql`
	query ClientAssetsPage_Query($projectId: String) {
		viewer {
			actualPersonId
			project(id: $projectId) {
				id
				...ClientAssetsPage_project
			}
		}
	}
`;

const fragment = graphql`
	fragment ClientAssetsPage_project on ProjectType {
		id
		tasks(first: 100000, simpleResponse: true) {
			edges {
				node {
					id
					name
				}
			}
		}
		clientAssets {
			id
			assetType
			name
			note
			status
			closureMode
			requiresReadAck
			dueDate
			psaTaskId
			taskName
			archived
			createdAt
			currentVersion {
				id
				versionNo
				fileName
				sizeBytes
				downloadUrl
				createdAt
				uploadedByFirm
				uploadedByEmail
			}
			versions {
				id
				versionNo
				fileName
				sizeBytes
				downloadUrl
				createdAt
				uploadedByFirm
				uploadedByEmail
			}
			responses {
				id
				name
				createdAt
				currentVersion {
					id
					fileName
					sizeBytes
					downloadUrl
					uploadedByEmail
					createdAt
				}
			}
			readReceipts {
				email
				versionNo
				readAt
			}
		}
	}
`;

// A plain div rather than the DS FlexColumn, which drops `data-cy`.
const PageWrapper = Styled.div`
	padding: 24px 32px;
`;

export type ClientAssetCreateType = 'deliverable' | 'reference' | 'input_request';
export type ClientAssetTask = {id: string; name: string};

export type ClientAssetsPageProps = {
	viewer: {project: ({id: string} & ClientAssetsPage_project$key) | null};
	retry?: () => void;
	/** Opens the create flow for one asset type; the buttons are no-ops while it is unset. */
	onNew?: (type: ClientAssetCreateType, ctx: {projectId: string; tasks: ClientAssetTask[]; refresh: () => void}) => void;
	/** Opens one asset's detail view; the row button is a no-op while it is unset. */
	onOpen?: (assetId: string, ctx: {projectId: string; refresh: () => void}) => void;
};

const FILTERS: AssetFilter[] = ['all', 'deliverable', 'reference', 'input_request'];
const FILTER_LABEL: Record<AssetFilter, string> = {
	all: 'client_assets.filter.all',
	deliverable: 'client_assets.filter.deliverables',
	reference: 'client_assets.filter.references',
	input_request: 'client_assets.filter.input_requests',
};

export const ClientAssetsPage = ({viewer, retry, onNew, onOpen}: ClientAssetsPageProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const project = useFragment(fragment, viewer.project);
	const [filter, setFilter] = useState<AssetFilter>('all');
	const assets = project?.clientAssets ?? null;
	const rows = useMemo(
		() => toRows(filterAssets(assets ?? [], filter), (id, values) => formatMessage({id}, values)),
		[assets, filter, formatMessage]
	);
	// Null means the field is not available to this viewer (soft-gated server-side).
	if (!project || assets === null) return null;

	const t = (id: string) => formatMessage({id});
	const tasks: ClientAssetTask[] = (project.tasks?.edges ?? []).flatMap(edge =>
		edge?.node ? [{id: edge.node.id, name: edge.node.name ?? ''}] : []
	);
	const refresh = () => retry?.();
	const openNew = (type: ClientAssetCreateType) => onNew?.(type, {projectId: project.id, tasks, refresh});

	return (
		<PageWrapper data-cy="client-assets-page">
			<FlexColumn gap="l">
				<FlexRow justifyContent="space-between">
					<FlexColumn gap="xs">
						<Heading size="3" as="h1">
							{t('client_assets.title')}
						</Heading>
						<Text size="2" color="auxiliary">
							{t('client_assets.subtitle')}
						</Text>
					</FlexColumn>
					<FlexRow gap="s">
						<Button emphasis="medium" onPress={() => openNew('reference')} data-cy="client-assets-new-reference">
							{t('client_assets.new_reference')}
						</Button>
						<Button
							emphasis="medium"
							onPress={() => openNew('input_request')}
							data-cy="client-assets-new-input-request"
						>
							{t('client_assets.new_input_request')}
						</Button>
						<Button emphasis="high" onPress={() => openNew('deliverable')} data-cy="client-assets-new-deliverable">
							{t('client_assets.new_deliverable')}
						</Button>
					</FlexRow>
				</FlexRow>
				<SegmentedControl
					label={t('client_assets.column.type')}
					value={filter}
					onValueChange={(next: string) => {
						// A click on the already-selected segment arrives as a deselect (an empty
						// string); the list always has a filter, so only a known segment applies.
						const known = FILTERS.find(f => f === next);
						if (known) setFilter(known);
					}}
					data-cy="client-assets-filters"
				>
					{FILTERS.map(f => (
						<SegmentedControl.Item
							key={f}
							value={f}
							label={t(FILTER_LABEL[f])}
							data-cy={`client-assets-filter-${f}`}
						>
							{t(FILTER_LABEL[f])}
						</SegmentedControl.Item>
					))}
				</SegmentedControl>
				{rows.length === 0 ? (
					<div data-cy="client-assets-empty">
						<Text color="auxiliary">{t('client_assets.empty')}</Text>
					</div>
				) : (
					<div data-cy="client-assets-table">
						<Table data={rows} width="100%" getRowId={(row: {id: string}) => row.id}>
							<Table.TextColumn accessorKey="name" header={t('client_assets.column.asset')} />
							<Table.TextColumn accessorKey="type" header={t('client_assets.column.type')} width="140px" />
							<Table.TextColumn accessorKey="version" header={t('client_assets.column.version')} width="90px" />
							<Table.TextColumn accessorKey="task" header={t('client_assets.column.task')} width="200px" />
							<Table.TextColumn accessorKey="status" header={t('client_assets.column.status')} width="200px" />
							<Table.TextColumn
								accessorKey="engagement"
								header={t('client_assets.column.engagement')}
								width="220px"
							/>
							<Table.ButtonColumn
								align={Table.Align.right}
								// The DS uses `header` as the column id, and an empty id makes the table throw.
								header={t('common.view')}
								hideHeader
								width="90px"
								button={(row: {id: string}) => ({
									label: t('common.view'),
									onClick: () => onOpen?.(row.id, {projectId: project.id, refresh}),
								})}
							/>
						</Table>
					</div>
				)}
			</FlexColumn>
		</PageWrapper>
	);
};

export default ClientAssetsPage;
